import { CustomPanel, DropdownPrimeReact } from "@finanedi.es/storybook"
import {
  faAngleDown,
  faAngleRight,
  faTimes,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TFunction } from "i18next"
import { Checkbox } from "primereact/checkbox"
import { InputText } from "primereact/inputtext"
import { useEffect, useState } from "react"
import { FieldErrors, UseFormSetValue } from "react-hook-form"
import { getProvinceIneOptions } from "utils/provinceDropdownOptions"
import {
  FieldTemplateOne,
  FieldTemplateThree,
  TEMPLATE_TYPE,
  renderFieldTemplateGeneric,
  renderFieldTemplateThree,
} from "../../../PlantillasBloquesTabla"
import { VALORES_CLAVE, VALORES_SUBCLAVE } from "./constants"
import { Modelo190Form } from "../DeclaracionModelo190"

const PerceptorsTemplate = ({
  t,
  text,
  values,
  setValue,
  perceptorIndex,
  handleDelete,
  errors,
}: {
  t: TFunction
  text: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  perceptorIndex: number
  handleDelete: () => void
  errors: FieldErrors<Modelo190Form>
}) => {
  const optionsPrestacion = [
    { label: t("modelo190.perceptors.prestacionNoSatisfecha"), value: "0" },
    { label: t("modelo190.perceptors.prestacionSatisfecha"), value: "1" },
  ]
  const handleClaveChange = (newClave: string) => {
    const valueUppercase = newClave.toUpperCase()
    setValue(`perceptores.${perceptorIndex}.clave`, valueUppercase)

    const isClaveB01 =
      valueUppercase === VALORES_CLAVE.B &&
      values?.perceptores?.[perceptorIndex]?.subclave === VALORES_SUBCLAVE.UNO

    if (!isClaveB01) {
      const fieldsToReset = [
        "tipoPrestacionClaveB01Jubilacion",
        "tipoPrestacionClaveB01Viudedad",
        "tipoPrestacionClaveB01Incapacidad",
        "tipoPrestacionClaveB01PensionesNoContributivas",
        "tipoPrestacionClaveB01Resto",
      ]

      fieldsToReset.forEach((field) => {
        setValue(`perceptores.${perceptorIndex}.${field}`, undefined)
      })
    }

    if (valueUppercase !== VALORES_CLAVE.A) {
      setValue(
        `perceptores.${perceptorIndex}.rendimientosTrabajoEmprendimientoInnovacion`,
        undefined
      )
    }
  }

  const handleSubclaveChange = (newSubclave: string) => {
    setValue(`perceptores.${perceptorIndex}.subclave`, newSubclave)

    const isClaveB01 =
      values?.perceptores?.[perceptorIndex]?.clave === VALORES_CLAVE.B &&
      newSubclave === VALORES_SUBCLAVE.UNO

    if (!isClaveB01) {
      const fieldsToReset = [
        "tipoPrestacionClaveB01Jubilacion",
        "tipoPrestacionClaveB01Viudedad",
        "tipoPrestacionClaveB01Incapacidad",
        "tipoPrestacionClaveB01PensionesNoContributivas",
        "tipoPrestacionClaveB01Resto",
      ]

      fieldsToReset.forEach((field) => {
        setValue(`perceptores.${perceptorIndex}.${field}`, undefined)
      })
    }
  }

  const fieldsPerceptors: FieldTemplateOne[] = [
    {
      label: t("taxSummary.declaracion.perceptorNif"),
      code: `perceptores.${perceptorIndex}.nif`,
      value: values?.perceptores?.[perceptorIndex]?.nif,
      inputType: "text",
    },
    {
      label: t("taxSummary.declaracion.legalRepresentative"),
      code: `perceptores.${perceptorIndex}.legalRepresentative`,
      value: values?.perceptores?.[perceptorIndex]?.legalRepresentative,
      inputType: "text",
    },
    {
      label: t("taxSummary.declaracion.perceptorName"),
      code: `perceptores.${perceptorIndex}.denominacion`,
      value: values?.perceptores?.[perceptorIndex]?.denominacion,
      inputType: "text",
      colSpan: "tw-col-span-2",
    },
    {
      label: t("taxSummary.declaracion.province"),
      code: `perceptores.${perceptorIndex}.provincia`,
      value: values?.perceptores?.[perceptorIndex]?.provincia,
      inputType: "dropdown",
      options: getProvinceIneOptions(t),
    },
  ]

  const fieldsPerceptorsTwo: FieldTemplateOne[] = [
    {
      label: t("taxSummary.declaracion.key"),
      code: `perceptores.${perceptorIndex}.clave`,
      value: values?.perceptores?.[perceptorIndex].clave,
      inputType: "text",
      className: "tw-uppercase",
      mandatory: true,
      error: errors?.perceptores?.[perceptorIndex]?.clave?.message,
      onChange: handleClaveChange,
    },
    {
      label: t("modelo190.perceptors.subKey"),
      code: `perceptores.${perceptorIndex}.subclave`,
      value: values?.perceptores?.[perceptorIndex]?.subclave,
      inputType: "text",
      mandatory: true,
      error: errors?.perceptores?.[perceptorIndex]?.subclave?.message,
      onChange: handleSubclaveChange,
    },
    {
      label: t("taxSummary.declaracion.ejercicioDevengo"),
      code: `perceptores.${perceptorIndex}.ejercicioDevengo`,
      value: values?.perceptores?.[perceptorIndex]?.ejercicioDevengo,
      inputType: "text",
    },
    {
      label: t("modelo190.perceptors.ceutaMelilla"),
      code: `perceptores.${perceptorIndex}.ceutaMelilla`,
      value: values?.perceptores?.[perceptorIndex]?.ceutaMelilla,
      inputType: "checkbox",
    },
  ]

  const fieldPerceptorsJubilacion: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.jubilacion"),
      code: `perceptores.${perceptorIndex}.tipoPrestacionClaveB01Jubilacion`,
      value:
        values?.perceptores?.[perceptorIndex].tipoPrestacionClaveB01Jubilacion,
      inputType: "dropdown",
      options: optionsPrestacion,
      disabled:
        values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.B ||
        values?.perceptores?.[perceptorIndex]?.subclave !==
          VALORES_SUBCLAVE.UNO,
    },
    {
      label: t("modelo190.perceptors.viudedad"),
      code: `perceptores.${perceptorIndex}.tipoPrestacionClaveB01Viudedad`,
      value:
        values?.perceptores?.[perceptorIndex].tipoPrestacionClaveB01Viudedad,
      inputType: "dropdown",
      options: optionsPrestacion,
      disabled:
        values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.B ||
        values?.perceptores?.[perceptorIndex]?.subclave !==
          VALORES_SUBCLAVE.UNO,
    },
    {
      label: t("modelo190.perceptors.incapacidad"),
      code: `perceptores.${perceptorIndex}.tipoPrestacionClaveB01Incapacidad`,
      value:
        values?.perceptores?.[perceptorIndex].tipoPrestacionClaveB01Incapacidad,
      inputType: "dropdown",
      colSpan: "tw-col-span-2",
      options: optionsPrestacion,
      disabled:
        values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.B ||
        values?.perceptores?.[perceptorIndex]?.subclave !==
          VALORES_SUBCLAVE.UNO,
    },
  ]

  const fieldPerceptorsPensiones: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.pensionesNoContributivas"),
      code: `perceptores.${perceptorIndex}.tipoPrestacionClaveB01PensionesNoContributivas`,
      value:
        values?.perceptores?.[perceptorIndex]
          .tipoPrestacionClaveB01PensionesNoContributivas,
      inputType: "dropdown",
      colSpan: "tw-col-span-2",
      options: optionsPrestacion,
      disabled:
        values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.B ||
        values?.perceptores?.[perceptorIndex]?.subclave !==
          VALORES_SUBCLAVE.UNO,
    },
    {
      label: t("modelo190.perceptors.restoPrestaciones"),
      code: `perceptores.${perceptorIndex}.tipoPrestacionClaveB01Resto`,
      value: values?.perceptores?.[perceptorIndex].tipoPrestacionClaveB01Resto,
      inputType: "dropdown",
      colSpan: "tw-col-span-2",
      options: optionsPrestacion,
      disabled:
        values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.B ||
        values?.perceptores?.[perceptorIndex]?.subclave !==
          VALORES_SUBCLAVE.UNO,
    },
  ]

  const fieldsPerceptorsThree: FieldTemplateThree[] = [
    {
      section: t("modelo190.perceptors.box1"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.percepcionIntegra")),
          code: `perceptores.${perceptorIndex}.percepcionesDinerariasNoDerivadasIncapacidadLaboral.percepcionIntegra`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.percepcionesDinerariasNoDerivadasIncapacidadLaboral
              ?.percepcionIntegra,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.retencionesPract")),
          code: `perceptores.${perceptorIndex}.percepcionesDinerariasNoDerivadasIncapacidadLaboral.retencionPractica`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.percepcionesDinerariasNoDerivadasIncapacidadLaboral
              ?.retencionPractica,
          inputType: "currency",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.box2"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.valoracion")),
          code: `perceptores.${perceptorIndex}.especieNoValoracion`,
          value: values?.perceptores?.[perceptorIndex]?.especieNoValoracion,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.ingresosACuentaEfectuados")),
          code: `perceptores.${perceptorIndex}.especieNoretencionesEfectuados`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.especieNoretencionesEfectuados,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.ingresosACuentaRepercutidos")),
          code: `perceptores.${perceptorIndex}.especieNoRepercutidos`,
          value: values?.perceptores?.[perceptorIndex]?.especieNoRepercutidos,
          inputType: "currency",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.box3"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.percepcionIntegra")),
          code: `perceptores.${perceptorIndex}.dinerariaPercepcionIntegra`,
          value:
            values?.perceptores?.[perceptorIndex]?.dinerariaPercepcionIntegra,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.retencionesPract")),
          code: `perceptores.${perceptorIndex}.dinerariaRetencionesPracticadas`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.dinerariaRetencionesPracticadas,
          inputType: "currency",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.box4"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.valoracion")),
          code: `perceptores.${perceptorIndex}.especieValoracion`,
          value: values?.perceptores?.[perceptorIndex]?.especieValoracion,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.ingresosACuentaEfectuados")),
          code: `perceptores.${perceptorIndex}.especieretencionesEfectuados`,
          value:
            values?.perceptores?.[perceptorIndex]?.especieretencionesEfectuados,
          inputType: "currency",
        },
        {
          label: String(t("modelo190.perceptors.ingresosACuentaRepercutidos")),
          code: `perceptores.${perceptorIndex}.especieRepercutidos`,
          value: values?.perceptores?.[perceptorIndex]?.especieRepercutidos,
          inputType: "currency",
        },
      ],
    },
  ]

  const fieldsPerceptorsFourth: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.box5"),
      code: `perceptores.${perceptorIndex}.haciendaEstatal`,
      value: values?.perceptores?.[perceptorIndex]?.haciendaEstatal,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.box6"),
      code: `perceptores.${perceptorIndex}.foralNavarra`,
      value: values?.perceptores?.[perceptorIndex]?.foralNavarra,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.box7"),
      code: `perceptores.${perceptorIndex}.foralAlava`,
      value: values?.perceptores?.[perceptorIndex].foralAlava,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.box8"),
      code: `perceptores.${perceptorIndex}.foralGipuzkoa`,
      value: values?.perceptores?.[perceptorIndex]?.foralGipuzkoa,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.box9"),
      code: `perceptores.${perceptorIndex}.foralBiskaia`,
      value: values?.perceptores?.[perceptorIndex]?.foralBiskaia,
      inputType: "currency",
    },
  ]

  const fieldsPerceptorsFith: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.nacimientoPerceptor"),
      code: `perceptores.${perceptorIndex}.nacimientoPerceptor`,
      value: values?.perceptores?.[perceptorIndex]?.nacimientoPerceptor,
      inputType: "text",
    },
    {
      label: t("modelo190.perceptors.situacionFamiliar"),
      code: `perceptores.${perceptorIndex}.situacionFamiliar`,
      value: values?.perceptores?.[perceptorIndex]?.situacionFamiliar,
      inputType: "text",
    },
    {
      label: t("modelo190.perceptors.nifConyuge"),
      code: `perceptores.${perceptorIndex}.nifConyuge`,
      value: values?.perceptores?.[perceptorIndex]?.nifConyuge,
      inputType: "text",
      colSpan: "tw-col-span-2",
    },
    {
      label: t("modelo190.perceptors.discapacidadPerceptor"),
      code: `perceptores.${perceptorIndex}.discapacidadPerceptor`,
      value: values?.perceptores?.[perceptorIndex]?.discapacidadPerceptor,
      inputType: "text",
    },
    {
      label: t("modelo190.perceptors.contratoORelacion"),
      code: `perceptores.${perceptorIndex}.contratoORelacion`,
      value: values?.perceptores?.[perceptorIndex]?.contratoORelacion,
      inputType: "text",
    },
    {
      label: t("modelo190.perceptors.titularUnidadConvivencia"),
      code: `perceptores.${perceptorIndex}.titularUnidadConvivencia`,
      value: values?.perceptores?.[perceptorIndex]?.titularUnidadConvivencia,
      inputType: "text",
      colSpan: "tw-col-span-2",
    },
  ]

  const fieldsPerceptorsSixth: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.movilidadGeografica"),
      code: `perceptores.${perceptorIndex}.movilidadGeografica`,
      value: values?.perceptores?.[perceptorIndex]?.movilidadGeografica,
    },
    {
      label: t("modelo190.perceptors.complementoAyuda"),
      code: `perceptores.${perceptorIndex}.complementoAyuda`,
      value: values?.perceptores?.[perceptorIndex]?.complementoAyuda,
    },
  ]

  const deduccionesField: FieldTemplateOne[] = [
    {
      label: t("modelo190.perceptors.reducciones"),
      code: `perceptores.${perceptorIndex}.reducciones`,
      value: values?.perceptores?.[perceptorIndex]?.reducciones,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.gastosDeducibles"),
      code: `perceptores.${perceptorIndex}.gastosDeducibles`,
      value: values?.perceptores?.[perceptorIndex]?.gastosDeducibles,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.pensiones"),
      code: `perceptores.${perceptorIndex}.pensiones`,
      value: values?.perceptores?.[perceptorIndex]?.pensionesCompensatorias,
      inputType: "currency",
    },
    {
      label: t("modelo190.perceptors.anualidadesAlimento"),
      code: `perceptores.${perceptorIndex}.anualidadesAlimento`,
      value: values?.perceptores?.[perceptorIndex]?.anualidadesAlimento,
      inputType: "currency",
    },
  ]

  const fieldsPerceptorsSeventh: FieldTemplateThree[] = [
    {
      section: t("modelo190.perceptors.hijos"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.menorTresAnos")),
          code: `perceptores.${perceptorIndex}.menorTresAnos`,
          value: values?.perceptores?.[perceptorIndex]?.menorTresAnos,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.porEntero`,
          value: values?.perceptores?.[perceptorIndex]?.porEntero,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.resto")),
          code: `perceptores.${perceptorIndex}.resto`,
          value: values?.perceptores?.[perceptorIndex]?.resto,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.porEnteroTwo`,
          value: values?.perceptores?.[perceptorIndex]?.porEnteroTwo,
          inputType: "number",
        },
      ],
      labelTwo: String(t("modelo190.perceptors.computo")),
      inputsTwo: [
        {
          label: String(t("modelo190.perceptors.first")),
          code: `perceptores.${perceptorIndex}.computiFirst`,
          value: values?.perceptores?.[perceptorIndex]?.computiFirst,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.second")),
          code: `perceptores.${perceptorIndex}.computiSecond`,
          value: values?.perceptores?.[perceptorIndex]?.computiSecond,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.third")),
          code: `perceptores.${perceptorIndex}.computiThird`,
          value: values?.perceptores?.[perceptorIndex]?.computiThird,
          inputType: "number",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.hijosDiscapacidad"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.porcentajeDiscapacidad")),
          code: `perceptores.${perceptorIndex}.porcentajeDiscapacidad`,
          value: values?.perceptores?.[perceptorIndex]?.porcentajeDiscapacidad,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.discapacidadPorEntero`,
          value: values?.perceptores?.[perceptorIndex]?.discapacidadPorEntero,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.molvilidadReducida")),
          code: `perceptores.${perceptorIndex}.molvilidadReducida`,
          value: values?.perceptores?.[perceptorIndex]?.molvilidadReducida,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.discapacidadPorEnteroTwo`,
          value:
            values?.perceptores?.[perceptorIndex]?.discapacidadPorEnteroTwo,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porcentajeDiscapacidadDos")),
          code: `perceptores.${perceptorIndex}.porcentajeDiscapacidadDos`,
          value:
            values?.perceptores?.[perceptorIndex]?.porcentajeDiscapacidadDos,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.discapacidadPorEnteroThree`,
          value:
            values?.perceptores?.[perceptorIndex]?.discapacidadPorEnteroThree,
          inputType: "number",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.ascendientes"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.menor75")),
          code: `perceptores.${perceptorIndex}.menor75`,
          value: values?.perceptores?.[perceptorIndex]?.menor75,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.ascendientesPorEntero`,
          value: values?.perceptores?.[perceptorIndex]?.ascendientesPorEntero,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.mayor75")),
          code: `perceptores.${perceptorIndex}.mayor75`,
          value: values?.perceptores?.[perceptorIndex]?.mayor75,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.ascendientesPorEnteroTwo`,
          value:
            values?.perceptores?.[perceptorIndex]?.ascendientesPorEnteroTwo,
          inputType: "number",
        },
      ],
    },
    {
      section: t("modelo190.perceptors.ascendientesDiscapacidad"),
      inputsOne: [
        {
          label: String(t("modelo190.perceptors.porcentajeDiscapacidad")),
          code: `perceptores.${perceptorIndex}.ascendientePorcentajeDiscapacidad`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.ascendientePorcentajeDiscapacidad,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.ascendientesDiscPorEntero`,
          value:
            values?.perceptores?.[perceptorIndex]?.ascendientesDiscPorEntero,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.molvilidadReducida")),
          code: `perceptores.${perceptorIndex}.ascendienteMolvilidadReducida`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.ascendienteMolvilidadReducida,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.ascendientesDiscPorEnteroTwo`,
          value:
            values?.perceptores?.[perceptorIndex]?.ascendientesDiscPorEnteroTwo,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porcentajeDiscapacidadDos")),
          code: `perceptores.${perceptorIndex}.ascendientePorcentajeDiscapacidadDos`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.ascendientePorcentajeDiscapacidadDos,
          inputType: "number",
        },
        {
          label: String(t("modelo190.perceptors.porEntero")),
          code: `perceptores.${perceptorIndex}.ascendientesDiscPorEnteroThree`,
          value:
            values?.perceptores?.[perceptorIndex]
              ?.ascendientesDiscPorEnteroThree,
          inputType: "number",
        },
      ],
    },
  ]
  const [categoryExpanded, setCategoryExpanded] = useState(true)
  const [categoryExpandedTwo, setCategoryExpandedTwo] = useState(true)

  const optionsRendimientoTrabajoFondosEmprendimiento = [
    { label: t("modelo190.perceptors.opcionRendimientos"), value: "0" },
    {
      label: t("modelo190.perceptors.opcionRendimientosUno"),
      value: "1",
    },
  ]

  const optionsExcesosAcciones = [
    { label: t("modelo190.perceptors.excesosAccionesUno"), value: "0" },
    { label: t("modelo190.perceptors.excesosAccionesDos"), value: "1" },
  ]

  const hasErrorClaveOrSubclave = errors?.perceptores?.[perceptorIndex]

  useEffect(() => {
    if (hasErrorClaveOrSubclave) {
      setCategoryExpanded(false)
    }
  }, [hasErrorClaveOrSubclave])

  return (
    <CustomPanel
      title={text}
      expandIcon={null}
      collapseIcon={null}
      className='[&>div:first-child]:tw-cursor-pointer tw-font-medium !tw-shadow-none tw-border [&>div:nth-child(2)]:!tw-max-h-[3000px]'
      collapsed={categoryExpanded}
      onToggle={(e) => setCategoryExpanded(e.value)}
      headerTemplate={() => (
        <div
          className='tw-flex tw-justify-between tw-px-4 tw-py-3'
          onClick={() => setCategoryExpanded((prevState) => !prevState)}
        >
          <p>{text}</p>
          <div>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
              className='hover:tw-bg-gray-100 tw-w-6 tw-rounded tw-transition-colors'
            >
              <FontAwesomeIcon
                icon={faTimes}
                className='tw-text-red-500'
              />
            </button>
            <button
              type='button'
              onClick={() => setCategoryExpanded((prevState) => !prevState)}
              className='tw-w-4 tw-ml-4'
            >
              <FontAwesomeIcon
                icon={categoryExpanded ? faAngleDown : faAngleRight}
              />
            </button>
          </div>
        </div>
      )}
    >
      <div className='tw-p-4'>
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
          {renderFieldTemplateGeneric({
            fields: fieldsPerceptors,
            setValue,
          })}
        </div>
        <div className='tw-text-sm tw-font-medium tw-py-3 tw-border-b tw-border-gray-200'>
          {t("modelo190.perceptors.perceptionData")}
        </div>
        <div className='tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-gray-200 tw-items-center'>
          {renderFieldTemplateGeneric({
            fields: fieldsPerceptorsTwo,
            setValue,
            t,
          })}
        </div>
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
          {renderFieldTemplateGeneric({
            fields: fieldPerceptorsJubilacion,
            setValue,
          })}
        </div>
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
          {renderFieldTemplateGeneric({
            fields: fieldPerceptorsPensiones,
            setValue,
          })}
        </div>
        <div className='tw-col-span-1 tw-flex tw-flex-col tw-py-3 tw-border-b tw-border-gray-200'>
          <span className='tw-text-xs tw-max-w- tw-font-medium tw-text-left'>
            {t("modelo190.perceptors.rendimientosTrabajo")}
          </span>
          <div className='!tw-w-full'>
            <DropdownPrimeReact
              value={
                values?.perceptores?.[perceptorIndex]
                  ?.rendimientosTrabajoEmprendimientoInnovacion
              }
              options={optionsRendimientoTrabajoFondosEmprendimiento}
              optionLabel='label'
              optionValue='value'
              onChange={(e) =>
                setValue(
                  `perceptores.${perceptorIndex}.rendimientosTrabajoEmprendimientoInnovacion`,
                  e.value
                )
              }
              disabled={
                values?.perceptores?.[perceptorIndex]?.clave !== VALORES_CLAVE.A
              }
              placeholder='Selecciona'
              className='!tw-w-full !tw-h-10 !tw-text-sm'
              panelClassName='tw-rounded-md tw-max-w-100'
              aria-label={String(t("modelo190.perceptors.excesosAcciones"))}
            />
          </div>
        </div>
        <div className='tw-col-span-1 tw-flex tw-flex-col tw-py-3 tw-border-b tw-border-gray-200'>
          <span className='tw-text-xs tw-max-w- tw-font-medium tw-text-left'>
            {t("modelo190.perceptors.excesosAcciones")}
          </span>
          <div className='!tw-w-full'>
            <DropdownPrimeReact
              value={values?.perceptores?.[perceptorIndex]?.excesoAcciones}
              options={optionsExcesosAcciones}
              optionLabel='label'
              optionValue='value'
              onChange={(e) =>
                setValue(
                  `perceptores.${perceptorIndex}.excesoAcciones`,
                  e.value
                )
              }
              placeholder='Selecciona'
              className='!tw-w-full !tw-h-10 !tw-text-sm'
              panelClassName='tw-rounded-md tw-max-w-100'
              aria-label={String(t("modelo190.perceptors.excesosAcciones"))}
            />
          </div>
        </div>
        {fieldsPerceptorsThree.map((field) =>
          renderFieldTemplateThree(
            field,
            setValue,
            TEMPLATE_TYPE.TEMPLATE_FORMAT_TWO,
            "tw-grid-cols-4",
            "tw-col-span-1"
          )
        )}
        <div className='tw-text-xs tw-text-left tw-w-full tw-py-3 tw-border-b tw-border-gray-200'>
          {t("modelo190.perceptors.text")}
        </div>
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
          {renderFieldTemplateGeneric({
            fields: fieldsPerceptorsFourth,
            setValue,
          })}
        </div>
        <CustomPanel
          title={`${t("modelo190.perceptors.aditionalData")}`}
          expandIcon={<FontAwesomeIcon icon={faAngleRight} />}
          collapseIcon={<FontAwesomeIcon icon={faAngleDown} />}
          className='[&>div:first-child]:tw-cursor-pointer tw-font-medium !tw-shadow-none [&>div:nth-child(2)]:!tw-max-h-[1100px] tw-overflow-scroll'
          collapsed={categoryExpandedTwo}
          onToggle={(e) => setCategoryExpandedTwo(e.value)}
          headerTemplate={({ element }) => (
            <div
              onClick={() => setCategoryExpandedTwo((prevState) => !prevState)}
            >
              {element}
            </div>
          )}
        >
          <div className='tw-text-sm tw-font-medium tw-py-3 tw-border-b tw-border-gray-200'>
            {t("modelo190.perceptors.aditionalDataKey")}
          </div>
          <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
            {renderFieldTemplateGeneric({
              fields: fieldsPerceptorsFith,
              setValue,
            })}
          </div>
          <div className='tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-gray-200 tw-items-center'>
            <div className='tw-flex tw-justify-between'>
              <span className='tw-text-sm'>
                {fieldsPerceptorsSixth[0].label}
              </span>
              <Checkbox
                checked={fieldsPerceptorsSixth[0].value}
                onChange={() =>
                  setValue(
                    `${fieldsPerceptorsSixth[0].code}`,
                    !fieldsPerceptorsSixth[0].value
                  )
                }
              />
            </div>
            <div className='tw-text-sm tw-col-span-2'>
              <div className='tw-text-left tw-text-xs tw-font-medium'>
                {fieldsPerceptorsSixth[1].label}
              </div>
              <InputText
                className='tw-w-full tw-text-sm'
                value={
                  fieldsPerceptorsSixth[1].value
                    ? String(fieldsPerceptorsSixth[1].value)
                    : ""
                }
                onChange={(e) =>
                  setValue(fieldsPerceptorsSixth[1].code, e.target.value)
                }
                disabled={fieldsPerceptorsSixth[1].disabled ?? false}
              />
            </div>
          </div>
          <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25 tw-text-center'>
            {renderFieldTemplateGeneric({
              fields: deduccionesField,
              setValue,
            })}
          </div>
          <div className='tw-py-3 tw-border-b tw-border-gray-200 tw-items-center'>
            <div className='tw-text-left tw-text-xs tw-font-medium'>
              {t("modelo190.perceptors.comunicacionPrestamo")}
            </div>
            <InputText
              className='tw-w-full tw-text-sm'
              value={
                values?.perceptores?.[perceptorIndex]?.comunicacionPrestamo
              }
              onChange={(e) =>
                setValue(
                  `perceptores.${perceptorIndex}.comunicacionPrestamo`,
                  e.target.value
                )
              }
            />
          </div>
          {fieldsPerceptorsSeventh.map((field) =>
            renderFieldTemplateThree(
              field,
              setValue,
              TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE,
              "tw-grid-cols-4",
              "",
              "tw-col-span-1 tw-pt-3"
            )
          )}
        </CustomPanel>
      </div>
    </CustomPanel>
  )
}

export default PerceptorsTemplate
